import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiServiciosInstance, authHeaderServicios } from "../../redux/actions/serviciosPrecios/serviciosPrecios";
import { usePermisos } from "../../context/PermisosContext";
import ActividadTab from "./ActividadTab";

import "./PacientesPage.css";

dayjs.locale("es");

const fmtFecha = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const money = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ServicioDetallePage() {
  const { id_servicio } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const fetchServicio = useCallback(() => {
    setLoading(true);
    apiServiciosInstance
      .get(`/servicios-precios/${id_servicio}`, { headers: authHeaderServicios() })
      .then((res) => setServicio(res.data))
      .catch(() => toast.error("No se pudo cargar el servicio"))
      .finally(() => setLoading(false));
  }, [id_servicio]);

  useEffect(() => { fetchServicio(); }, [fetchServicio]);

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Eliminar este servicio?",
      content: "Se desactivará y dejará de aparecer en el catálogo.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        setDeleting(true);
        try {
          await apiServiciosInstance.delete(`/servicios-precios/${id_servicio}`, { headers: authHeaderServicios() });
          setDeleteSuccess(true);
        } catch (err) {
          toast.error(err?.response?.data?.detail || err.message || "No se pudo eliminar el servicio");
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!servicio) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Servicio no encontrado" />
      </div>
    );
  }

  const activo = servicio.active === undefined ? true : Boolean(servicio.active);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(servicio.nombre || "—").toUpperCase()}</h1>
                <span className={`cd-status-badge ${activo ? "cd-status-active" : "cd-status-inactive"}`}>
                  {activo ? "Activo" : "Inactivo"}
                </span>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item">
                  <i className="fa-solid fa-calendar" />
                  Creado el {fmtFecha(servicio.datetime)}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              {perm("servicios_precios", "eliminar") && (
                <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Información
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
        <div className="cd-card">
          <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-tags" /></div>
              <h2 className="cd-card-title">Información del servicio</h2>
            </div>
            {perm("servicios_precios", "editar") && (
              <Button className="cd-btn-edit" onClick={() => navigate(`/app/servicios/${id_servicio}/editar`)}>
                Editar
              </Button>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
            <div>
              <span className="cd-field-label">Nombre</span>
              <span className="cd-field-value">{servicio.nombre}</span>
            </div>
            <div>
              <span className="cd-field-label">Precio de lista</span>
              <span className="cd-field-value">{money(servicio.precio_lista)}</span>
            </div>
            <div>
              <span className="cd-field-label">Fecha de creación</span>
              <span className="cd-field-value">{fmtFecha(servicio.datetime)}</span>
            </div>
            <div>
              <span className="cd-field-label">Estado</span>
              <span className="cd-field-value" style={{ color: activo ? "#15803d" : "#b45309" }}>
                {activo ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>
        </div>
        )}

        {activeTab === "actividad" && (
          <ActividadTab
            titulo="Actividad del servicio"
            fetcher={() => apiServiciosInstance.get(`/servicios-precios/${id_servicio}/actividad`, { headers: authHeaderServicios() })}
          />
        )}
      </div>

      <SuccessOverlay show={deleteSuccess} title="Servicio eliminado" onDone={() => navigate("/app/servicios")} />
    </div>
  );
}
